/**
 * Os pedaços de interface que o escritório e o tablet dividem.
 *
 * Tudo é montado com classes do design system (`mac-*`) mais as extensões de
 * produção (`pcp-*`). Nenhum componente inventa cor ou medida: quem decide é
 * `estilo/tahoe.css`.
 */

import {
  formato,
  type EstadoEtapa,
  type EstadoOs,
  type ItemDaFila,
  type StatusEtapa,
} from '@pcp/dominio';
import type { LinhaCarteira } from '@pcp/dados';
import { h, sf, type Filho } from './dom.js';

/* ------------------------------------------------------------------ */
/* Semáforo                                                            */
/* ------------------------------------------------------------------ */

type Situacao =
  | 'fila'
  | 'producao'
  | 'pausada'
  | 'pronta'
  | 'concluida'
  | 'atrasada'
  | 'risco'
  | 'cancelada';

const COR: Record<Situacao, string> = {
  fila: 'var(--pcp-fila)',
  producao: 'var(--pcp-producao)',
  pausada: 'var(--pcp-pausada)',
  pronta: 'var(--pcp-pronta)',
  concluida: 'var(--pcp-concluida)',
  atrasada: 'var(--pcp-atrasada)',
  risco: 'var(--pcp-risco)',
  cancelada: 'var(--pcp-cancelada)',
};

const PALAVRA: Record<Situacao, string> = {
  fila: 'Em fila',
  producao: 'Em produção',
  pausada: 'Pausada',
  pronta: 'Pronta',
  concluida: 'Concluída',
  atrasada: 'Atrasada',
  risco: 'Em risco',
  cancelada: 'Cancelada',
};

/** A situação que importa mostrar: atraso e risco ganham do status cru. */
export function situacaoDaOs(linha: {
  estado: Pick<EstadoOs, 'status'>;
  atrasada: boolean;
  emRisco: boolean;
}): Situacao {
  if (linha.estado.status === 'cancelada') return 'cancelada';
  if (linha.estado.status === 'concluida') return 'concluida';
  if (linha.atrasada) return 'atrasada';
  if (linha.estado.status === 'pronta') return 'pronta';
  if (linha.emRisco) return 'risco';
  if (linha.estado.status === 'em_producao') return 'producao';
  return 'fila';
}

export function situacaoDaEtapa(status: StatusEtapa): Situacao {
  if (status === 'em_execucao') return 'producao';
  if (status === 'pausada') return 'pausada';
  if (status === 'concluida') return 'pronta';
  if (status === 'pulada') return 'concluida';
  return 'fila';
}

export function semaforo(
  situacao: Situacao,
  opcoes: { texto?: string | undefined; grande?: boolean | undefined; forte?: boolean | undefined } = {},
): HTMLElement {
  const classes = ['pcp-semaforo', situacao];
  if (opcoes.grande) classes.push('grande');
  if (opcoes.forte) classes.push('forte');
  return h(
    `.${classes.join('.')}`,
    { style: { '--pcp-cor': COR[situacao] } },
    opcoes.texto ?? PALAVRA[situacao],
  );
}

/* ------------------------------------------------------------------ */
/* Marcas                                                              */
/* ------------------------------------------------------------------ */

export function marca(
  tipo: 'prioridade' | 'alterada' | 'triagem' | 'erp',
  texto: string,
  icone: string,
): HTMLElement {
  return h(`.pcp-marca.${tipo}`, { title: texto }, [sf(icone), texto]);
}

export function marcasDaOs(linha: LinhaCarteira): HTMLElement[] {
  const saida: HTMLElement[] = [];
  if (linha.os.prioridadeEm) saida.push(marca('prioridade', 'Prioridade', 'flag-fill'));
  if (linha.temEtapaMarcada) saida.push(marca('alterada', 'OS alterada', 'exclamationmark-triangle'));
  if (linha.temEtapaAClassificar) saida.push(marca('triagem', 'A classificar', 'questionmark-circle'));
  if (linha.os.concluidaNoErp) saida.push(marca('erp', 'Concluída no ERP', 'checkmark-circle'));
  return saida;
}

/* ------------------------------------------------------------------ */
/* Progresso                                                           */
/* ------------------------------------------------------------------ */

export function progresso(
  etapas: ReadonlyArray<{ estado: Pick<EstadoEtapa, 'status'> }>,
): HTMLElement {
  return h(
    '.pcp-progresso',
    { title: `${etapas.filter((e) => e.estado.status === 'concluida' || e.estado.status === 'pulada').length} de ${etapas.length} etapas` },
    etapas.map((e) => {
      const s = e.estado.status;
      const classe =
        s === 'concluida' || s === 'pulada'
          ? 'feita'
          : s === 'em_execucao'
            ? 'correndo'
            : s === 'pausada'
              ? 'parada'
              : '';
      return h(`i${classe ? `.${classe}` : ''}`);
    }),
  );
}

/* ------------------------------------------------------------------ */
/* Cartão de OS                                                        */
/* ------------------------------------------------------------------ */

export interface OpcoesCartao {
  aoClicar?: ((numero: number) => void) | undefined;
  mostrarEtapa?: string | null | undefined;
  selecionado?: boolean | undefined;
  rodapeExtra?: Filho | undefined;
}

export function cartaoOs(linha: LinhaCarteira, opcoes: OpcoesCartao = {}): HTMLElement {
  const situacao = situacaoDaOs(linha);
  const classes = ['pcp-os'];
  if (opcoes.selecionado) classes.push('is-selected');

  const conteudo: Filho = [
    h('.pcp-os-topo', [
      h('span.pcp-os-numero', String(linha.os.numero)),
      h('span.pcp-os-cliente', linha.os.clienteCurto),
      semaforo(situacao),
    ]),
    h('.pcp-os-produto', `${formato.numero(linha.os.quantidade)} · ${linha.os.produto}`),
    h('.pcp-os-rodape', [
      h('span', { title: formato.dataHoraLonga(linha.os.previsaoEntregaEm) }, [
        sf('calendar'),
        ' ',
        formato.dataHoraCurta(linha.os.previsaoEntregaEm),
      ]),
      opcoes.mostrarEtapa
        ? h('span', { title: 'Etapa' }, [sf('list-bullet'), ' ', opcoes.mostrarEtapa])
        : linha.setorAtual
          ? h('span', [sf('location'), ' ', linha.setorAtual.nome])
          : null,
      h('span.spacer'),
      ...marcasDaOs(linha),
      opcoes.rodapeExtra ?? null,
    ]),
  ];

  const estilo = { '--pcp-cor': COR[situacao] };

  if (opcoes.aoClicar) {
    const numero = linha.os.numero;
    return h(
      `button.${classes.join('.')}`,
      { style: estilo, type: 'button', onclick: () => opcoes.aoClicar?.(numero) },
      conteudo,
    );
  }
  return h(`.${classes.join('.')}`, { style: estilo }, conteudo);
}

/* ------------------------------------------------------------------ */
/* Tempos previsto × real                                              */
/* ------------------------------------------------------------------ */

export function tempos(previstoSeg: number | null, realSeg: number): HTMLElement {
  const pct =
    previstoSeg && previstoSeg > 0 ? ((realSeg - previstoSeg) / previstoSeg) * 100 : null;
  const classe = pct === null ? '' : pct > 10 ? 'acima' : pct < -10 ? 'abaixo' : '';
  return h('.pcp-tempos', [
    h('span.previsto', { title: 'Tempo previsto na OS' }, formato.hhmm(previstoSeg)),
    h('span.real', { title: 'Tempo medido' }, formato.hhmm(realSeg)),
    pct !== null && realSeg > 0
      ? h(`span.desvio${classe ? `.${classe}` : ''}`, formato.desvio(pct))
      : null,
  ]);
}

/* ------------------------------------------------------------------ */
/* Estado vazio, aviso                                                 */
/* ------------------------------------------------------------------ */

export function vazio(
  icone: string,
  titulo: string,
  ajuda?: string | undefined,
  acao?: Filho | undefined,
): HTMLElement {
  return h('.vazio', [
    sf(icone),
    h('h2', titulo),
    ajuda ? h('p', ajuda) : null,
    acao ? h('div', { style: { marginTop: '12px' } }, acao) : null,
  ]);
}

export function aviso(
  tipo: 'aviso' | 'erro' | 'ok' | 'info',
  texto: Filho,
  acao?: Filho | undefined,
): HTMLElement {
  const icone =
    tipo === 'erro'
      ? 'exclamationmark-triangle-fill'
      : tipo === 'ok'
        ? 'checkmark-circle-fill'
        : tipo === 'info'
          ? 'info-circle'
          : 'exclamationmark-triangle';
  const classe = { erro: 'a-perigo', ok: 'a-ok', info: 'a-info', aviso: 'a-alerta' }[tipo];
  return h(`.aviso.${classe}`, [sf(icone), h('div.spacer.aviso-c', texto), acao ?? null]);
}

/* ------------------------------------------------------------------ */
/* Indicador de conexão                                                */
/* ------------------------------------------------------------------ */

export interface EstadoDaConexao {
  online: boolean;
  pendentes: number;
  travados: number;
  ultimoSucessoEm: string | null;
  desde: string | null;
}

/**
 * Está sempre visível, nos dois aplicativos.
 *
 * Não é enfeite: se o operador não souber que está sem conexão, ele assume que
 * gravou, e um tablet reiniciado antes de sincronizar perde o turno (lição do
 * Flux).
 */
export function indicadorDeConexao(e: EstadoDaConexao): HTMLElement {
  if (e.travados > 0) {
    return h('.pcp-conexao.travado', [
      sf('exclamationmark-triangle-fill'),
      `${e.travados} ${e.travados === 1 ? 'travado' : 'travados'} — chame o encarregado`,
    ]);
  }
  if (!e.online) {
    const desde = e.desde ? ` desde ${formato.hora(e.desde)}` : '';
    const fila = e.pendentes > 0 ? ` · ${e.pendentes} ${e.pendentes === 1 ? 'pendente' : 'pendentes'}` : '';
    return h('.pcp-conexao.offline', [sf('bolt'), `Sem conexão${desde}${fila}`]);
  }
  if (e.pendentes > 0) {
    return h('.pcp-conexao', [
      sf('arrow-triangle-2-circlepath'),
      `Enviando ${e.pendentes}…`,
    ]);
  }
  return h('.pcp-conexao.online', [
    sf('checkmark-circle'),
    e.ultimoSucessoEm ? `Sincronizado ${formato.distanciaNoTempo(e.ultimoSucessoEm)}` : 'Online',
  ]);
}

/* ------------------------------------------------------------------ */
/* Botões e utilidades                                                 */
/* ------------------------------------------------------------------ */

export function botao(
  rotulo: string,
  aoClicar: () => void,
  opcoes: {
    icone?: string | undefined;
    estilo?: 'primary' | 'destructive' | 'plain' | 'glass' | undefined;
    pequeno?: boolean | undefined;
    desabilitado?: boolean | undefined;
    titulo?: string | undefined;
  } = {},
): HTMLElement {
  // O vocabulário da frota: `.bt` é a cápsula, `.bt-p` o principal, `.bt-d` o
  // destrutivo, `.bt-f` o fantasma. `.bt-s` (secundário, com sombra) é o padrão
  // quando ninguém pede estilo — é o botão de barra do macOS.
  const porEstilo = { primary: 'bt-p', destructive: 'bt-d', plain: 'bt-f', glass: 'bt-s' } as const;
  const classes = ['bt', opcoes.estilo ? porEstilo[opcoes.estilo] : 'bt-s'];
  if (opcoes.pequeno) classes.push('bt-p2');
  return h(
    `button.${classes.join('.')}`,
    {
      type: 'button',
      onclick: aoClicar,
      disabled: opcoes.desabilitado,
      title: opcoes.titulo,
    },
    [opcoes.icone ? sf(opcoes.icone) : null, rotulo],
  );
}

/** Título de seção com a régua da frota: nome, filete, ação à direita. */
export function tituloDeSecao(texto: string, acao?: Filho | undefined): HTMLElement {
  return h('.secao', [
    h('.secao-t', texto),
    h('.secao-l'),
    acao ?? null,
  ]);
}

/** Uma métrica grande e clicável do painel. */
export function metrica(o: {
  valor: string | number;
  rotulo: string;
  detalhe?: string | undefined;
  cor?: string | undefined;
  aoClicar?: (() => void) | undefined;
}): HTMLElement {
  const conteudo: Filho = [
    h('.valor', String(o.valor)),
    h('.rotulo', o.rotulo),
    o.detalhe ? h('.detalhe', o.detalhe) : null,
  ];
  const props = { style: o.cor ? { '--pcp-cor': o.cor } : {} };
  return o.aoClicar
    ? h('button.pcp-metrica', { ...props, type: 'button', onclick: o.aoClicar }, conteudo)
    : h('.pcp-metrica', props, conteudo);
}

/** Item de fila no formato do quadro. */
export function cartaoDaFila(
  item: ItemDaFila,
  aoClicar?: ((numero: number) => void) | undefined,
): HTMLElement {
  const situacao = situacaoDaEtapa(item.estado.status);
  const linha: LinhaCarteira = {
    os: item.os,
    estado: {
      status: item.os.status,
      prontaEm: null,
      etapasConcluidas: 0,
      etapasTotal: 0,
      tempoPrevistoRestanteSeg: 0,
      tempoPrevistoTotalSeg: 0,
      tempoRealTotalSeg: 0,
      etapaAtualId: item.etapa.id,
    },
    atrasada: Date.parse(item.os.previsaoEntregaEm) < Date.now(),
    emRisco: false,
    setorAtual: null,
    estacaoAtual: null,
    etapaAtual: item.etapa,
    operadores: item.estado.operadores,
    temEtapaAClassificar: item.etapa.aClassificar,
    temEtapaMarcada: item.etapa.marcadaVerificar,
  };

  const cartao = cartaoOs(linha, {
    aoClicar,
    mostrarEtapa: item.etapa.nomeOs,
    rodapeExtra:
      item.estado.status === 'em_execucao' || item.estado.status === 'pausada'
        ? semaforo(situacao)
        : item.etapa.tempoPrevistoSeg
          ? h('span', [sf('clock'), ' ', formato.hhmm(item.etapa.tempoPrevistoSeg)])
          : null,
  });
  return cartao;
}

export type { Situacao };

/**
 * O cabeçalho de página da frota: sobrancelha, título e uma linha de contexto.
 *
 * É a assinatura visual do Flux e do Amparo, e não é decoração. Numa lista
 * densa de OS, sem ele a primeira coisa que o olho encontra é um filtro ou uma
 * célula de tabela, e a pessoa precisa olhar o trilho para saber onde está. A
 * `lead` responde à pergunta que o nome da tela não responde — "Quadro" não
 * diz que ele atualiza sozinho, nem que uma coluna é um setor.
 *
 * As ações da página vão à direita, nunca na barra de ferramentas: a barra é
 * do aplicativo, o cabeçalho é da tela.
 */
export function cabecalhoDePagina(o: {
  olho: string;
  titulo: string;
  lead?: string | undefined;
  acoes?: Filho | undefined;
}): HTMLElement {
  return h('.ph', [
    h('div', [
      h('p.olho', o.olho.toUpperCase()),
      h('h1', o.titulo),
      o.lead ? h('p.lead', o.lead) : null,
    ]),
    o.acoes ? h('.ph-acoes', o.acoes) : null,
  ]);
}

/* ------------------------------------------------------------------ */
/* Abertura                                                            */
/* ------------------------------------------------------------------ */

/**
 * A tela de abertura: o papel de parede do sistema com uma folha de vidro.
 *
 * É a mesma linguagem do Run e do Flux: em vez de uma tela cinza com um anel
 * girando — que parece o aplicativo ter sumido —, a marca respira enquanto o
 * app monta, o motivo fica escrito e um traço vai e volta. O mesmo desenho
 * serve para a espera e para o erro (`tom: 'aviso'`).
 */
export function telaDeAbertura(opcoes: {
  /** Nome grande na folha. */
  titulo?: string | undefined;
  /** O motivo, em português de gente. */
  descricao?: Filho | undefined;
  /** Ícone do sistema (nome do SF Symbol). */
  icone?: string | undefined;
  /** `aviso` pinta o ícone de vermelho para as telas de "não dá para seguir". */
  tom?: 'normal' | 'aviso' | undefined;
  /** Mostra o traço de carga pulsando em vez do ícone parado. */
  carregando?: boolean | undefined;
  /** Botões (Tentar de novo, Sair…). */
  extra?: Filho | undefined;
  /** Largura máxima da folha. */
  largura?: number | undefined;
} = {}): HTMLElement {
  const {
    titulo = 'PCP',
    descricao,
    icone = 'list-bullet',
    tom = 'normal',
    carregando = true,
    extra,
    largura = 420,
  } = opcoes;

  const classeIcone = ['mac-app-icon', 'lg', 'pcp-abertura-icone'];
  if (tom === 'aviso') classeIcone.push('aviso');
  if (carregando) classeIcone.push('pulsa');

  return h('.pcp-abertura', [
    h('.mac-wallpaper', { 'aria-hidden': 'true' }),
    h(
      '.glass-strong.glass-panel.anim-pop.pcp-abertura-folha',
      { style: { maxWidth: `${largura}px` }, role: tom === 'aviso' ? 'alert' : undefined },
      [
        h(`span.${classeIcone.join('.')}`, sf(icone)),
        h('div', [
          h('h1.pcp-abertura-titulo', titulo),
          descricao ? h('p.pcp-abertura-desc', descricao) : null,
        ]),
        carregando ? h('.pcp-carga', h('i')) : null,
        extra ? h('.pcp-abertura-extra', extra) : null,
      ],
    ),
  ]);
}
